import { useMemo, useState } from "react";
import { api, ApiError } from "../api/client";
import { useQuery } from "../api/useQuery";
import { fmtDateTime } from "../components/format";
import type { Territory, User } from "../api/types";

const KINDS: Record<string, string> = {
  transport: "Транспорт",
  radio: "Радиостанция",
  special: "Спецсредства",
  other: "Другое",
};

const CONDITIONS: Record<string, string> = {
  ok: "Исправно",
  damaged: "Неисправно",
  incomplete: "Некомплект",
  missing: "Отсутствует",
};

interface Item {
  id: string;
  territory_id: string;
  territory_name: string;
  kind: string;
  inventory_no: string;
  name: string;
  condition: string;
  assigned_to: string | null;
  assigned_name: string | null;
  note: string;
  is_active: boolean;
  last_phase: "issue" | "return" | null;
  last_inspector: string | null;
  last_at: string | null;
  last_condition: string | null;
  can_edit: boolean;
}

type Form = Omit<Item, "id" | "territory_name" | "assigned_name" | "last_phase" | "last_inspector" | "last_at" | "last_condition" | "can_edit">;

/**
 * Реестр имущества: транспорт, радиостанции, спецсредства. Где вещь сейчас,
 * видно по чек-листам смен: их связывает инвентарный номер.
 */
export function EquipmentPage() {
  const items = useQuery<{ items: Item[] }>("/api/v1/equipment-items");
  const territories = useQuery<{ items: Territory[] }>("/api/v1/territories");
  const users = useQuery<{ items: User[] }>("/api/v1/users");
  const [kind, setKind] = useState("");
  const [selected, setSelected] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);

  const visible = useMemo(
    () => (items.data?.items ?? []).filter((i) => !kind || i.kind === kind),
    [items.data, kind]);
  const current = items.data?.items.find((i) => i.id === selected) ?? null;
  const issued = (items.data?.items ?? []).filter((i) => i.last_phase === "issue").length;

  return (
    <>
      <div className="page-head">
        <div>
          <h2>Имущество</h2>
          {items.data && (
            <p className="muted small">{items.data.items.length} единиц · сейчас выдано по чек-листам: {issued}</p>
          )}
        </div>
        <button onClick={() => { setCreating(true); setSelected(null); }}>Добавить</button>
      </div>

      <div className="filters">
        <div className="segmented" role="group" aria-label="Вид">
          <button aria-pressed={kind === ""} onClick={() => setKind("")}>Всё</button>
          {Object.entries(KINDS).map(([k, t]) => (
            <button key={k} aria-pressed={kind === k} onClick={() => setKind(k)}>{t}</button>
          ))}
        </div>
      </div>

      {items.error && <div className="error" role="alert">{items.error}</div>}

      <div className="split">
        <div className="split-list">
          <div className="list">
            {items.data && visible.length === 0 && <div className="empty">Имущество не заведено</div>}
            {visible.map((i) => (
              <button key={i.id} className={`item${i.id === selected && !creating ? " selected" : ""}`}
                onClick={() => { setSelected(i.id); setCreating(false); }}>
                <span className="item-body">
                  <span className="item-head">
                    <span className="item-title">{i.name}</span>
                    {!i.is_active && <span className="badge">списано</span>}
                    {i.condition !== "ok" && <span className="badge danger">{CONDITIONS[i.condition]?.toLowerCase()}</span>}
                    {i.last_phase === "issue" && <span className="badge warn">выдано</span>}
                  </span>
                  <span className="item-text">
                    {KINDS[i.kind]} · № {i.inventory_no} · {i.territory_name}
                    {i.assigned_name && ` · закреплено: ${i.assigned_name}`}
                  </span>
                </span>
              </button>
            ))}
          </div>
        </div>

        <div className="split-detail">
          {creating && (
            <ItemForm key="new" title="Новая единица"
              initial={{
                territory_id: territories.data?.items[0]?.id ?? "", kind: "radio", inventory_no: "", name: "",
                condition: "ok", assigned_to: null, note: "", is_active: true,
              }}
              territories={territories.data?.items ?? []} users={users.data?.items ?? []}
              onDone={(id) => { items.reload(); setCreating(false); setSelected(id); }}
              onCancel={() => setCreating(false)} />
          )}
          {!creating && current && (
            <section className="card">
              <div className="muted small">{KINDS[current.kind]} · {current.territory_name}</div>
              <h3 style={{ fontSize: 22, margin: "2px 0 12px" }}>{current.name}</h3>
              <div className="list" style={{ background: "var(--bg)", marginBottom: 16 }}>
                <div className="row">
                  <span className="muted">По чек-листам смен</span>
                  <span>
                    {current.last_phase
                      ? `${current.last_phase === "issue" ? "выдано" : "сдано"}: ${current.last_inspector}, ${fmtDateTime(current.last_at)}` +
                        (current.last_condition && current.last_condition !== "ok" ? ` (${CONDITIONS[current.last_condition]?.toLowerCase()})` : "")
                      : "в чек-листах не встречалось"}
                  </span>
                </div>
              </div>
              {current.can_edit ? (
                <ItemForm key={current.id} title={null} itemId={current.id}
                  initial={{
                    territory_id: current.territory_id, kind: current.kind, inventory_no: current.inventory_no,
                    name: current.name, condition: current.condition, assigned_to: current.assigned_to,
                    note: current.note, is_active: current.is_active,
                  }}
                  territories={territories.data?.items ?? []} users={users.data?.items ?? []}
                  onDone={() => items.reload()} />
              ) : (
                <p className="muted small">Изменять имущество этой территории вы не можете.</p>
              )}
            </section>
          )}
          {!creating && !current && <div className="card empty">Выберите единицу или добавьте новую</div>}
        </div>
      </div>
    </>
  );
}

function ItemForm({ title, initial, itemId, territories, users, onDone, onCancel }: {
  title: string | null;
  initial: Form;
  itemId?: string;
  territories: Territory[];
  users: User[];
  onDone: (id: string) => void;
  onCancel?: () => void;
}) {
  const [form, setForm] = useState<Form>(initial);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const changed = JSON.stringify(form) !== JSON.stringify(initial);
  const staff = users.filter((u) => u.is_active && u.territory_id === form.territory_id);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      if (itemId) {
        await api.put(`/api/v1/equipment-items/${itemId}`, form);
        onDone(itemId);
      } else {
        const res = await api.post<{ id: string }>("/api/v1/equipment-items", form);
        onDone(res.id);
      }
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Не удалось сохранить");
    } finally {
      setBusy(false);
    }
  }

  const fields = (
    <form onSubmit={(e) => void submit(e)} style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      {error && <div className="error" role="alert">{error}</div>}
      {!itemId && territories.length > 1 && (
        <label className="field">Территория
          <select value={form.territory_id} onChange={(e) => setForm({ ...form, territory_id: e.target.value, assigned_to: null })}>
            {territories.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select>
        </label>
      )}
      <div className="filters" style={{ margin: 0 }}>
        <label className="field">Вид
          <select value={form.kind} onChange={(e) => setForm({ ...form, kind: e.target.value })}>
            {Object.entries(KINDS).map(([k, t]) => <option key={k} value={k}>{t}</option>)}
          </select>
        </label>
        <label className="field">Инвентарный номер
          <input required value={form.inventory_no} onChange={(e) => setForm({ ...form, inventory_no: e.target.value })} />
        </label>
      </div>
      <label className="field">Наименование
        <input required value={form.name} placeholder="Например: УАЗ Патриот А 123 ВС или Радиостанция Аргут" onChange={(e) => setForm({ ...form, name: e.target.value })} />
      </label>
      <div className="filters" style={{ margin: 0 }}>
        <label className="field">Состояние
          <select value={form.condition} onChange={(e) => setForm({ ...form, condition: e.target.value })}>
            {Object.entries(CONDITIONS).map(([k, t]) => <option key={k} value={k}>{t}</option>)}
          </select>
        </label>
        <label className="field">Закреплено за
          <select value={form.assigned_to ?? ""} onChange={(e) => setForm({ ...form, assigned_to: e.target.value || null })}>
            <option value="">не закреплено</option>
            {staff.map((u) => <option key={u.id} value={u.id}>{u.full_name}</option>)}
          </select>
        </label>
      </div>
      <label className="field">Примечание
        <input value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} />
      </label>
      {itemId && (
        <label style={{ display: "flex", gap: 8, alignItems: "center", fontSize: 14 }}>
          <input type="checkbox" checked={form.is_active} style={{ minHeight: 0 }}
            onChange={(e) => setForm({ ...form, is_active: e.target.checked })} />
          На балансе — снятая с учёта единица остаётся в истории
        </label>
      )}
      <div className="filters" style={{ margin: 0 }}>
        <button type="submit" disabled={busy || !changed}>{itemId ? "Сохранить" : "Добавить"}</button>
        {onCancel && <button type="button" className="secondary" onClick={onCancel}>Отмена</button>}
      </div>
    </form>
  );
  return title ? <section className="card"><h3>{title}</h3>{fields}</section> : fields;
}
